import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { parsePieveLinkRecipientToken } from '@shared/utils';
import toastManager from '@/lib/toast/toastManager.js';
import switchOrganization from '@/redux/actions/sessionActions/switchOrganization.js';
import { activeCompany, userCompanies } from '@/redux/selectors/userSelectors.js';

/**
 * Links in Pieve emails carry `?share=`, naming the workspace the email is about. Before the page
 * loads anything, this moves the person to that workspace when it is another of theirs, then drops
 * the key from the address. The token grants no access; the API still checks every read.
 */
const LinkRecipientGate = ({ children }) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const company = useSelector(activeCompany);
  const companies = useSelector(userCompanies);
  const handledToken = useRef(null);
  const shareToken = searchParams.get('share');

  useEffect(() => {
    if (!shareToken || handledToken.current === shareToken) {
      return;
    }

    handledToken.current = shareToken;
    const dropShareKey = () => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete('share');
          return next;
        },
        { replace: true }
      );
    };
    const recipient = parsePieveLinkRecipientToken(shareToken);
    const target = companies.find((item) => item.id === recipient?.companyId);

    if (recipient && !target) {
      toastManager.add({ type: 'error', title: 'This link is for another workspace', description: 'You are not a member of the workspace it belongs to.' });
    }

    if (!target || target.id === company?.id) {
      dropShareKey();
      return;
    }

    dispatch(switchOrganization({ companyId: target.id })).then((res) => {
      if (res?.success === false) {
        toastManager.add({ type: 'error', title: 'Could not switch workspace', description: res.message });
      }

      dropShareKey();
    });
  }, [shareToken, companies, company?.id, dispatch, setSearchParams]);

  return shareToken ? null : children;
};

export default LinkRecipientGate;
